import { later, next } from '@ember/runloop';
import EmberService, { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';

import type { Service } from 'webapp-types';
import { BillingFeature } from 'webapp/enums/enums';
import { uniq } from 'webapp/utils/common';

import type { BoardRecord } from 'webapp/models';
import type { CustomQuery, Metric } from 'webapp/models-custom';
import type BatchSelect from 'webapp/models-custom/batch-select';
import Source from 'webapp/models-custom/source';
import SourceType from 'webapp/models-custom/source-type';

import type { AfterConnectFn } from 'webapp/services/connect';

interface Step {
	id: StepId;
	link: string;
	canTransit: boolean;
	order: number;
	name: string;
	isHidden: boolean;
	isCompleted: boolean;
	isDisabled: boolean;
	isSelected?: boolean;
}

export enum StepId {
	SOURCE = 'source',
	STARTING = 'starting',
	CONNECT = 'connect',
	METRICS = 'metrics',
	CREATE_METRIC = 'create-metric',
	CREATE_BLOCK = 'create-block',
}

export default class WizardService extends EmberService {
	@service('billing') declare billingService: Service.Billing;
	@service('navigation') declare navigationService: Service.Navigation;
	@service('request') declare requestService: Service.Request;
	@service('model-manager/custom-queries')
	declare mmCustomQueriesService: Service.ModelManager.CustomQueries;
	@service('model-manager/source-types')
	declare mmSourceTypesService: Service.ModelManager.SourceTypes;
	@service('flow/tour/onboarding')
	declare flowTourOnboardingService: Service.Flow.Tour.Onboarding;
	@service('model-manager/metrics') declare mmMetricsService: Service.ModelManager.Metrics;

	@tracked activeStep: StepId | null = null;
	@tracked selectedSpecificSourceId: string | null = null;
	@tracked title: string | null = null;
	@tracked hasUserChosenQbFlow = false;
	@tracked preselectedSourceForDesigner = '';
	@tracked sourcesBatchSelect: BatchSelect<SourceType> | null = null;

	visitedSteps: StepId[] = [StepId.SOURCE];

	createdBoard: BoardRecord | null = null;

	metricsBatchSelect: BatchSelect<Metric> | null = null;
	selectedMetricsBySourceKey: Record<string, Metric[]> = {};

	tagForNewBoard: number | null = null; // if we create board from home, this is the active tag that will be added to the new board

	// used in create-metric step for qb-only sources
	createdCustomQuery: CustomQuery | null = null;

	get createdCustomMetric() {
		return this.createdCustomQuery?.metric || null;
	}

	get selectedSourceType(): SourceType | null {
		return this.sourcesBatchSelect?.selectedItems?.[0] || null;
	}

	get metricsForSelectedSource() {
		return this.getMetricsForSource(this.selectedSourceType);
	}

	get showStepsOnWizardStarting() {
		return (
			this.selectedSourceType?.isPremium && !this.billingService.checkIfCan(BillingFeature.QB)
		);
	}

	get isQbOnlyFlow() {
		return this.selectedSourceType?.isQbOnly || this.hasUserChosenQbFlow;
	}

	get allSteps() {
		const activeStep = this.activeStep,
			selectedSpecificSourceId = this.selectedSpecificSourceId,
			steps: Step[] = [];

		this.addStep(steps, StepId.SOURCE, 1, 'Choose data source');

		if (this.isQbOnlyFlow) {
			if (this.showStepsOnWizardStarting) {
				this.addStep(steps, StepId.STARTING, 1, 'Choose data source', true);
			}

			this.addStep(steps, StepId.CONNECT, 2, 'Connect your data');

			if (
				selectedSpecificSourceId &&
				this.mmCustomQueriesService.customQueryModels.filter(
					(item) => item.sourceId === +selectedSpecificSourceId,
				).length
			) {
				this.addStep(steps, StepId.METRICS, 3, 'Select custom metric');
			} else {
				this.addStep(steps, StepId.CREATE_METRIC, 3, 'Build custom metric');
				this.addStep(steps, StepId.CREATE_BLOCK, 4, 'Add metric to a dashboard');
			}
		} else {
			if (this.metricsForSelectedSource.length) {
				this.addStep(steps, StepId.METRICS, 2, 'Select metrics');
			} else {
				this.addStep(steps, StepId.STARTING, 2, 'Select metrics');
			}

			this.addStep(steps, StepId.CONNECT, 3, 'Connect your data');
		}

		// set selected
		if (activeStep) {
			const step = steps.find((item) => item.id === activeStep);
			if (step) {
				step.isSelected = true;
			}
		}

		return steps;
	}

	get availableSources() {
		let availableSources = this.mmSourceTypesService.connectableSourceTypeModels.filter(
				(item) => item.isNative && !item.isHidden,
			),
			stableSources = [],
			betaSources = [],
			soonSources = [];

		stableSources = availableSources.filter((item) => !item.isBeta && !item.isSoon);
		betaSources = availableSources.filter((item) => item.isBeta);
		soonSources = availableSources.filter((item) => item.isSoon);
		availableSources = uniq([...stableSources, ...betaSources, ...soonSources]);

		// preload images
		availableSources
			.map((item) => item.avatarBig)
			.map((img) => {
				new Image().src = img;
			});

		return availableSources;
	}

	get isWideView() {
		const activeStep = this.activeStep;

		return (
			activeStep &&
			(['create-metric', 'create-block'].includes(activeStep) ||
				(activeStep === 'connect' && this.isQbOnlyFlow))
		);
	}

	getMetricsForSource(sourceType: SourceType | null) {
		let metrics: Metric[] = [];

		if (sourceType) {
			const selectedSpecificSourceId = this.selectedSpecificSourceId;

			if (sourceType.isQbOnly && selectedSpecificSourceId) {
				metrics =
					this.mmMetricsService.getMetricsForSpaceAccessId(+selectedSpecificSourceId);
			} else {
				metrics = this.mmMetricsService.getGenericMetricsForSourceTypeKey(sourceType.key);
			}

			metrics = metrics.filter((metric: Metric) => !metric.isDeprecated);
		}

		return metrics;
	}

	canTransit(stepId: StepId) {
		return this.visitedSteps.includes(stepId) && this.activeStep !== stepId;
	}

	isStepCompleted(stepId: StepId) {
		const activeStep = this.activeStep,
			numericIndex = this.visitedSteps.indexOf(stepId),
			numericIndexOfActiveStep = activeStep ? this.visitedSteps.indexOf(activeStep) : null;

		return (
			numericIndex !== -1 &&
			!!numericIndexOfActiveStep &&
			numericIndexOfActiveStep > numericIndex
		);
	}

	addStep(steps: Step[], id: StepId, num: number, name: string, isHidden = false) {
		steps.push({
			id,
			link: `wizard.${id}`,
			canTransit: this.canTransit(id),
			order: num,
			name,
			isHidden, // not shown in step tabs
			isCompleted: this.isStepCompleted(id),
			isDisabled: !this.visitedSteps.includes(id),
		});
	}

	clearSelection(step: string) {
		if (['source', 'metrics'].includes(step)) {
			const sourceKeys = Object.keys(this.selectedMetricsBySourceKey);

			if (sourceKeys.length) {
				sourceKeys.forEach(
					(sourceKey) => delete this.selectedMetricsBySourceKey[sourceKey],
				);
				this.metricsBatchSelect?.unselectAll();
			}
		}
	}

	requestSource() {
		this.mmSourceTypesService.openSourceRequestForm();
	}

	connectSource(sourceKey: string, afterConnect: AfterConnectFn) {
		const sourceType = this.mmSourceTypesService.findModelByKey(sourceKey);

		void sourceType?.connectNew({ origin: 'wizard' }).then((connectResult) => {
			if (afterConnect) {
				next(
					afterConnect,
					connectResult.isSuccess,
					connectResult.isSuccess ? connectResult.source : undefined,
				); // on next so we have time to have pushed it in the store
			}
		});
	}

	createNewDataboard(source: Source | SourceType) {
		const currentSource = source || this.selectedSourceType;

		let preselectId = '';
		if (source instanceof Source) {
			preselectId = source.id!;
		} else if (currentSource instanceof SourceType) {
			preselectId = currentSource.key;
		}

		// because we can go directly to new databoard with preselected datasource in datablock library,
		// information about selected source is set on board controller
		if (this.billingService.checkIfCan(BillingFeature.BOARDS, true)) {
			this.preselectedSourceForDesigner = preselectId;
			this.navigationService.transitionToRoute('designer');
		}
	}

	setCreatedBoard(board: BoardRecord) {
		const tagForNewBoard = this.tagForNewBoard;

		this.createdBoard = board;
		if (tagForNewBoard) {
			board.tags = [tagForNewBoard];
			board
				.save()
				.catch(this.requestService.showGenericValidationError.bind(this.requestService));
		}
	}

	adjustVisitedSteps() {
		const visitedSteps = this.visitedSteps,
			activeStep = this.activeStep;

		if (activeStep && visitedSteps.includes(activeStep)) {
			this.visitedSteps = visitedSteps.slice(0, visitedSteps.indexOf(activeStep) + 1);
		}
	}

	goToStep(step: StepId) {
		this.clearSelection(step);
		this.adjustVisitedSteps();
		if (!this.visitedSteps.includes(step)) {
			this.visitedSteps = [...this.visitedSteps, step];
		}
		return this.navigationService.transitionToRoute('wizard.' + step);
	}

	goToNext() {
		this.adjustVisitedSteps();
		const allSteps = this.allSteps,
			selectedStepIdx = allSteps.findIndex((step) => step.isSelected),
			nextStep = allSteps[selectedStepIdx + 1];

		if (!nextStep) {
			const createdBoard = this.createdBoard;
			if (createdBoard) {
				return this.navigationService.goToBoardsAndOpenPreview(createdBoard).then(() => {
					if (this.flowTourOnboardingService.isBannerShown) {
						later(() => {
							this.flowTourOnboardingService.continueTour();
						}, 500);
					}
				});
			} else {
				return Promise.reject();
			}
		} else {
			return this.goToStep(nextStep.id);
		}
	}

	goToPrevious() {
		const activeStep = this.activeStep;

		if (activeStep && !['create-block'].includes(activeStep)) {
			this.hasUserChosenQbFlow = false;
		}
		window.history.go(-1);
	}

	goToSources() {
		this.goToStep(StepId.SOURCE);
	}

	cleanUp() {
		this.activeStep = null;
		this.title = null;
		this.createdBoard = null;
		this.tagForNewBoard = null;
		this.selectedSpecificSourceId = null;
		this.createdCustomQuery = null;
		this.selectedMetricsBySourceKey = {};
		this.visitedSteps = [StepId.SOURCE];

		if (this.metricsBatchSelect) {
			this.metricsBatchSelect.unselectAll();
		}

		if (this.sourcesBatchSelect) {
			this.sourcesBatchSelect.unselectAll();
		}
	}
}

declare module '@ember/service' {
	interface Registry {
		wizard: WizardService;
	}
}
